'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const QuickQuoteSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-ocean to-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="CalculatorIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm font-body font-semibold">Instant Quote</span>
            </div>
            
            <h2 className="text-3xl lg:text-5xl font-headline font-bold mb-6">
              Get Your Moving Quote in Seconds
            </h2>
            
            <p className="text-lg lg:text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
              No hidden fees, no surprises. Our transparent pricing calculator gives you an accurate estimate instantly.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                href="/instant-quote"
                className="flex items-center space-x-2 px-8 py-4 bg-accent text-slate-800 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft w-full sm:w-auto justify-center"
              >
                <Icon name="CalculatorIcon" size={24} variant="solid" />
                <span>Calculate Your Quote</span>
              </Link>
              
              <a
                href="https://wa.me/96569911181"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 bg-success text-white rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft w-full sm:w-auto justify-center"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
                <span>WhatsApp Us</span>
              </a>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Icon name="ClockIcon" size={32} variant="solid" className="text-accent mb-3" />
                <h3 className="font-headline font-bold text-xl mb-2">Instant Results</h3>
                <p className="text-white/80 font-body text-sm">
                  Get your quote immediately without waiting for callbacks
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Icon name="CurrencyDollarIcon" size={32} variant="solid" className="text-accent mb-3" />
                <h3 className="font-headline font-bold text-xl mb-2">No Hidden Fees</h3>
                <p className="text-white/80 font-body text-sm">
                  Transparent pricing with all costs clearly outlined
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Icon name="DocumentCheckIcon" size={32} variant="solid" className="text-accent mb-3" />
                <h3 className="font-headline font-bold text-xl mb-2">Accurate Estimates</h3>
                <p className="text-white/80 font-body text-sm">
                  Precise calculations based on your specific requirements
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-ocean to-primary text-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Icon name="CalculatorIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-body font-semibold">{t({ en: 'Instant Quote', ar: 'عرض سعر فوري' })}</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold mb-6">
            {t({ en: 'Get Your Moving Quote in Seconds', ar: 'احصل على عرض النقل في ثواني' })}
          </h2>
          
          <p className="text-lg lg:text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
            {t({ en: 'No hidden fees, no surprises. Our transparent pricing calculator gives you an accurate estimate instantly.', ar: 'لا رسوم مخفية، لا مفاجآت. حاسبة التسعير الشفافة لدينا تعطيك تقديراً دقيقاً على الفور.' })}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              href="/instant-quote"
              className="flex items-center space-x-2 px-8 py-4 bg-accent text-slate-800 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft w-full sm:w-auto justify-center"
            >
              <Icon name="CalculatorIcon" size={24} variant="solid" />
              <span>{t({ en: 'Calculate Your Quote', ar: 'احسب عرضك' })}</span>
            </Link>
            
            <a
              href="https://wa.me/96569911181"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-8 py-4 bg-success text-white rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-bold text-lg shadow-soft w-full sm:w-auto justify-center"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
              <span>{t({ en: 'WhatsApp Us', ar: 'واتساب' })}</span>
            </a>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Icon name="ClockIcon" size={32} variant="solid" className="text-accent mb-3" />
              <h3 className="font-headline font-bold text-xl mb-2">{t({ en: 'Instant Results', ar: 'نتائج فورية' })}</h3>
              <p className="text-white/80 font-body text-sm">
                {t({ en: 'Get your quote immediately without waiting for callbacks', ar: 'احصل على عرضك على الفور دون انتظار الردود' })}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Icon name="CurrencyDollarIcon" size={32} variant="solid" className="text-accent mb-3" />
              <h3 className="font-headline font-bold text-xl mb-2">{t({ en: 'No Hidden Fees', ar: 'لا رسوم مخفية' })}</h3>
              <p className="text-white/80 font-body text-sm">
                {t({ en: 'Transparent pricing with all costs clearly outlined', ar: 'تسعير شفاف مع توضيح جميع التكاليف بوضوح' })}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Icon name="DocumentCheckIcon" size={32} variant="solid" className="text-accent mb-3" />
              <h3 className="font-headline font-bold text-xl mb-2">{t({ en: 'Accurate Estimates', ar: 'تقديرات دقيقة' })}</h3>
              <p className="text-white/80 font-body text-sm">
                {t({ en: 'Precise calculations based on your specific requirements', ar: 'حسابات دقيقة بناءً على متطلباتك المحددة' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickQuoteSection;